import type { ReactNode } from "react";
import { createContext, useContext, useMemo, useState } from "react";

export interface ImpersonationState {
  organization: string | null;
  region: string | null;
  location: string | null;
}

interface ImpersonationContextType extends ImpersonationState {
  setImpersonation: (updates: Partial<ImpersonationState>) => void;
  clearImpersonation: () => void;
}

const ImpersonationContext = createContext<ImpersonationContextType>({
  organization: null,
  region: null,
  location: null,
  setImpersonation: () => {},
  clearImpersonation: () => {},
});

export function ImpersonationProvider({
  children,
}: {
  readonly children: ReactNode;
}) {
  const [state, setState] = useState<ImpersonationState>({
    organization: null,
    region: null,
    location: null,
  });

  const contextValue = useMemo(
    () => ({
      ...state,
      setImpersonation: (updates: Partial<ImpersonationState>) =>
        setState((prev) => ({ ...prev, ...updates })),
      clearImpersonation: () =>
        setState({ organization: null, region: null, location: null }),
    }),
    [state],
  );

  return (
    <ImpersonationContext.Provider value={contextValue}>
      {children}
    </ImpersonationContext.Provider>
  );
}

// Custom hook for using impersonation
export function useImpersonation() {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error(
      "useImpersonation must be used within ImpersonationProvider",
    );
  }
  return context;
}
